var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { config, Transition } from '@react-spring/web';
import cx from 'classnames';
import { getPosition, getRect, defaultTooltipPosition, getAnimationConfig, } from './utils';
import useOnClickOutside from '../hooks/use-click-outside';
import { useTooltip } from './tooltip-context';
import { TooltipContentContainer, TooltipContainer } from './styles';
const TooltipContent = React.forwardRef((_a, ref) => {
    var _b;
    var { placement = 'bottom', offset = 4 } = _a, props = __rest(_a, ["placement", "offset"]);
    const [rect, setRect] = useState(defaultTooltipPosition);
    const { showArrow, animation } = useTooltip();
    const animationConfig = getAnimationConfig(animation);
    const updateRect = () => {
        const position = getPosition(placement, getRect(props.parent, props.getPopupContainer), offset, showArrow, animation);
        setRect(position);
    };
    useEffect(() => {
        updateRect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.visible]);
    if (typeof window === 'undefined') {
        return null;
    }
    return createPortal(React.createElement(Transition, { items: props.visible, from: Object.assign({}, animationConfig === null || animationConfig === void 0 ? void 0 : animationConfig.from), enter: Object.assign({}, animationConfig === null || animationConfig === void 0 ? void 0 : animationConfig.enter), leave: Object.assign({}, animationConfig === null || animationConfig === void 0 ? void 0 : animationConfig.leave), config: config.stiff, delay: 200 }, (styles, item) => item && (React.createElement(TooltipContentContainer, { "data-role": "tooltip-content", className: "tooltip-content", transparent: true, top: rect.top, left: rect.left, style: Object.assign(Object.assign({}, styles), { transform: rect.transform, transformOrigin: rect.transformOrigin }), ref: ref, showArrow: typeof showArrow === 'boolean' ? showArrow : false }, props.children))), ((_b = props.getPopupContainer) === null || _b === void 0 ? void 0 : _b.call(props)) || document.body);
});
TooltipContent.displayName = 'TooltipContent';
export const Tooltip = (_a) => {
    var { trigger = 'hover', defaultVisible = false, placement = 'bottom' } = _a, props = __rest(_a, ["trigger", "defaultVisible", "placement"]);
    const parent = useRef(null);
    const child = useRef(null);
    const hasControlled = 'visible' in props;
    const { outsideCloseable } = useTooltip();
    const [visible, setVisible] = useState(defaultVisible);
    const controlledVisible = useMemo(() => {
        return hasControlled ? props.visible : visible;
    }, [visible, props.visible, hasControlled]);
    const handleMouseEnter = () => {
        var _a;
        if (trigger === 'hover') {
            setVisible(true);
            (_a = props.onMouseEnter) === null || _a === void 0 ? void 0 : _a.call(props);
        }
    };
    const handleClick = () => {
        var _a;
        if (trigger === 'click') {
            setVisible(true);
            (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
        }
    };
    useOnClickOutside(child, () => {
        var _a;
        (_a = props.onClickOutside) === null || _a === void 0 ? void 0 : _a.call(props);
        setVisible(false);
    }, { disable: !outsideCloseable, includes: [] });
    return (React.createElement(TooltipContainer, { className: cx('tooltip', props.className), "data-role": "tooltip", ref: parent, onClick: handleClick, onMouseEnter: handleMouseEnter, style: props.style, id: props.id },
        props.children,
        React.createElement(TooltipContent, { visible: controlledVisible, placement: placement, parent: parent, ref: child, offset: props.offset, debugKey: props.debugKey, getPopupContainer: props.getPopupContainer }, props.content)));
};
